import React, { lazy, Suspense, useContext } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import './App.css';

import { AuthProvider, AuthContext } from './context/AuthContext';
import { FetchProvider } from './context/FetchContext';

import AppShell from './AppShell';

import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import FourOFour from './pages/FourOFour';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';

import logo from './images/logo.png';

const Company = lazy(() => import('./pages/Company'));
const CompanyEdit = lazy(() => import('./pages/CompanyEdit'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RetrieveDocs = lazy(() => import('./pages/RetrieveDocs'));
const Send = lazy(() => import('./pages/Send'));
const SendInv = lazy(() => import('./pages/SendInv'));
const Account = lazy(() => import('./pages/Account'));
const Settings = lazy(() => import('./pages/Settings'));
const UserList = lazy(() => import('./pages/UserList'));
const UserEdit = lazy(() => import('./pages/UserEdit'));
const Archive = lazy(() => import('./pages/Archive'));
const Omit = lazy(() => import('./pages/Omit'));

const LoadingFallback = () => (
  <AppShell>
    <div className="p-4">Loading...</div>
  </AppShell>
);

const UnauthenticatedRoutes = () => (
  <Switch>
    <Route path="/login">
      <Login />
    </Route>
    <Route path="/signup">
      <Signup />
    </Route>
    <Route path="/forgot-password">
      <ForgotPassword />
    </Route>
    <Route path="/reset/:id/">
      <ResetPassword />
    </Route>
    <Route exact path="/">
      <Home />
    </Route>
    <Route path="*">
      <FourOFour />
    </Route>
  </Switch>
);

const AuthenticatedRoute = ({ children, ...rest }) => {
  const { authState } = useContext(AuthContext);
  return (
    <Route
      {...rest}
      render={() =>
        authState.isAuthenticated ? (
          <AppShell>{children}</AppShell>
        ) : (
          <Redirect to="/" />
        )
      }
    ></Route>
  );
};

const AdminRoute = ({ children, ...rest }) => {
  const { authState } = useContext(AuthContext);
  return (
    <Route
      {...rest}
      render={() =>
        authState.isAuthenticated && authState.userInfo.role === 'admin' ? (
          <AppShell>{children}</AppShell>
        ) : (
          <Redirect to="/" />
        )
      }
    ></Route>
  );
};

const LoadingLogo = () => {
  return (
    <div className="self-center">
      <img className="w-32" src={logo} alt="logo" />
    </div>
  );
};

const AppRoutes = () => {
  const { authState } = useContext(AuthContext);
  if (!authState.userInfo) {
    return (
      <div className="h-screen flex justify-center">
        <LoadingLogo />
      </div>
    );
  }
  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Switch>
          <AdminRoute path="/company">
            <Company />
          </AdminRoute>
          <AdminRoute path="/company-edit">
            <CompanyEdit />
          </AdminRoute>
          <AdminRoute path="/dash">
            <Dashboard />
          </AdminRoute>
          <AdminRoute path="/archive">
            <Archive />
          </AdminRoute>
          <AdminRoute path="/documents">
            <RetrieveDocs />
          </AdminRoute>
          <AdminRoute path="/send">
            <Send />
          </AdminRoute>
          <AdminRoute path="/sendinv">
            <SendInv />
          </AdminRoute>
          <AdminRoute path="/user-list">
            <UserList />
          </AdminRoute>
          <AdminRoute path="/user-edit">
            <UserEdit />
          </AdminRoute>
          <AdminRoute path="/omit">
            <Omit />
          </AdminRoute>
          <AuthenticatedRoute path="/account">
            <Account />
          </AuthenticatedRoute>
          <UnauthenticatedRoutes />
        </Switch>
      </Suspense>
    </>
  );
};

function App() {
  return (
    <Router>
      <FetchProvider>
        <AuthProvider>
          <div className="bg-gray-100">
            <AppRoutes />
          </div>
        </AuthProvider>
      </FetchProvider>
    </Router>
  );
}

export default App;
